import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../utils/api";
import styles from "./ViewOrganizationPage.module.scss";
import TabbedFormLayout from "../../components/TabbedFormLayout/TabbedFormLayout";
import { AxiosResponse } from "../../types";

// Define interfaces for type safety
interface Organization {
  id: string;
  name: string;
  adminuserid: string;
  primary_payment_account_id: string;
}

interface OrganizationInfo {
  full_business_name: string;
  business_dba: string;
  business_address: string;
  main_business_phone: string;
  fax_number: string;
  support_email: string;
  business_type: string;
  business_industry: string;
  website_url: string;
  established_date: string;
  tax_id_number: string;
  registration_number: string;
  operating_hours: {
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
  business_logo_url: string;
}

interface User {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  systemrole: string;
  roleid: number | null;
  apps: { app_id: string; access_level: string }[];
}

interface App {
  id: string;
  name: string;
}

interface Role {
  id: number;
  rolename: string;
  organizationid: string;
  permissions: Record<string, boolean>;
}

interface AxiosError extends Error {
  message: string;
}

export default function ViewOrganizationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [orgData, setOrgData] = useState<Organization>({
    id: "",
    name: "",
    adminuserid: "",
    primary_payment_account_id: "",
  });
  const [orgInfo, setOrgInfo] = useState<OrganizationInfo>({
    full_business_name: "",
    business_dba: "",
    business_address: "",
    main_business_phone: "",
    fax_number: "",
    support_email: "",
    business_type: "",
    business_industry: "",
    website_url: "",
    established_date: "",
    tax_id_number: "",
    registration_number: "",
    operating_hours: { monday: "", tuesday: "", wednesday: "", thursday: "", friday: "", saturday: "", sunday: "" },
    business_logo_url: "",
  });
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [apps, setApps] = useState<App[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  // Fetch organization data
  const fetchOrg = async () => {
    try {
      const [orgRes, infoRes, usersRes, appsRes, rolesRes] = await Promise.all([
        api.post<AxiosResponse<Organization>>("/core/organization/get", { organizationId: id }).catch(() => ({
          data: { id: "", name: "", adminuserid: "", primary_payment_account_id: "" },
          status: 200,
          statusText: "OK",
          headers: {},
          config: { headers: {} }
        } as AxiosResponse<Organization>)),
        api.post<AxiosResponse<OrganizationInfo>>("/core/organization/organization_info/get", { organizationid: id }).catch(() => ({
          data: {
            full_business_name: "",
            business_dba: "",
            business_address: "",
            main_business_phone: "",
            fax_number: "",
            support_email: "",
            business_type: "",
            business_industry: "",
            website_url: "",
            established_date: "",
            tax_id_number: "",
            registration_number: "",
            operating_hours: { monday: "", tuesday: "", wednesday: "", thursday: "", friday: "", saturday: "", sunday: "" },
            business_logo_url: ""
          },
          status: 200,
          statusText: "OK",
          headers: {},
          config: { headers: {} }
        } as AxiosResponse<OrganizationInfo>)),
        api.post<AxiosResponse<{ users: User[] }>>("/core/organization/users", { organizationId: id }).catch(() => ({
          data: { users: [] as User[] },
          status: 200,
          statusText: "OK",
          headers: {},
          config: { headers: {} }
        } as AxiosResponse<{ users: User[] }>)),
        api.post<AxiosResponse<App[]>>("/core/organization/app/list", {}).catch(() => ({
          data: [] as App[],
          status: 200,
          statusText: "OK",
          headers: {},
          config: { headers: {} }
        } as AxiosResponse<App[]>)),
        api.post<AxiosResponse<Role[]>>("/core/organization/orgroles", { organizationId: id }).catch(() => ({
          data: [] as Role[],
          status: 200,
          statusText: "OK",
          headers: {},
          config: { headers: {} }
        } as AxiosResponse<Role[]>))
      ]);

      const org = orgRes as AxiosResponse<Organization>;
      const info = infoRes as AxiosResponse<OrganizationInfo>;
      const users = usersRes as AxiosResponse<{ users: User[] }>;
      const apps = appsRes as AxiosResponse<App[]>;
      const roles = rolesRes as AxiosResponse<Role[]>;

      if (!org.data.id) throw new Error("Organization not found");

      setOrgData({
        id: org.data.id,
        name: org.data.name || "",
        adminuserid: org.data.adminuserid || "",
        primary_payment_account_id: org.data.primary_payment_account_id || ""
      });

      setOrgInfo({
        full_business_name: info.data.full_business_name || "",
        business_dba: info.data.business_dba || "",
        business_address: info.data.business_address || "",
        main_business_phone: info.data.main_business_phone || "",
        fax_number: info.data.fax_number || "",
        support_email: info.data.support_email || "",
        business_type: info.data.business_type || "",
        business_industry: info.data.business_industry || "",
        website_url: info.data.website_url || "",
        established_date: info.data.established_date || "",
        tax_id_number: info.data.tax_id_number || "",
        registration_number: info.data.registration_number || "",
        operating_hours: info.data.operating_hours || {
          monday: "", tuesday: "", wednesday: "", thursday: "", friday: "", saturday: "", sunday: ""
        },
        business_logo_url: info.data.business_logo_url || ""
      });

      setTeamMembers(users.data.users.map((user: User) => ({
        ...user,
        apps: user.apps ? user.apps.map((app: any) => {
          const [app_id, access_level] = typeof app === 'string' ? app.split(':') : [app.app_id, app.access_level];
          return { app_id, access_level };
        }) : []
      })) || []);

      setApps(apps.data);
      setRoles(roles.data);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load organization data. Some features may be unavailable.";
      console.error("Failed to fetch organization:", err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchOrg();
  }, [id]);

  const renderRow = (label: string, value: any) => (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{value !== null && value !== "" ? value : "—"}</span>
    </div>
  );

  if (loading) return <div className={styles.loading}>Loading...</div>;
  if (error && !orgData.name) return <div className={styles.error}>{error}</div>;

  const tabs = [
    {
      label: "Details",
      content: (
        <div className={styles.tabContent}>
          {renderRow("Name", orgData.name)}
          {renderRow("Admin User ID", orgData.adminuserid)}
          {renderRow("Primary Payment Account ID", orgData.primary_payment_account_id)}
          {renderRow("Full Business Name", orgInfo.full_business_name)}
          {renderRow("Business DBA", orgInfo.business_dba)}
          {renderRow("Business Address", orgInfo.business_address)}
          {renderRow("Main Business Phone", orgInfo.main_business_phone)}
          {renderRow("Fax Number", orgInfo.fax_number)}
          {renderRow("Support Email", orgInfo.support_email)}
          {renderRow("Business Type", orgInfo.business_type)}
          {renderRow("Business Industry", orgInfo.business_industry)}
          {renderRow("Website URL", orgInfo.website_url)}
          {renderRow("Established Date", orgInfo.established_date)}
          {renderRow("Tax ID Number", orgInfo.tax_id_number)}
          {renderRow("Registration Number", orgInfo.registration_number)}
          <div className={styles.row}>
            <span className={styles.label}>Operating Hours</span>
            <div className={styles.value}>
              {Object.keys(orgInfo.operating_hours).map((day) => (
                <div key={day}>
                  {day.charAt(0).toUpperCase() + day.slice(1)}: {orgInfo.operating_hours[day as keyof OrganizationInfo['operating_hours']] || "—"}
                </div>
              ))}
            </div>
          </div>
          {orgInfo.business_logo_url && (
            <div className={styles.row}>
              <span className={styles.label}>Business Logo</span>
              <span className={styles.value}>
                <img src={orgInfo.business_logo_url} alt="Business Logo" className={styles.logoPreview} />
              </span>
            </div>
          )}
        </div>
      )
    },
    {
      label: "Team Members",
      content: (
        <div className={styles.tabContent}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {teamMembers.map((member: User) => (
                <tr key={member.id}>
                  <td>{`${member.firstname} ${member.lastname}`}</td>
                  <td>{member.email}</td>
                  <td>
                    {member.roleid
                      ? roles.find((role) => role.id === member.roleid)?.rolename || "Custom Role"
                      : member.systemrole || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    },
    {
      label: "App Access",
      content: (
        <div className={styles.tabContent}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>App</th>
                <th>Access Level</th>
              </tr>
            </thead>
            <tbody>
              {teamMembers.map((member: User) => (
                apps.map((app: App) => {
                  const access = member.apps?.find((a) => a.app_id === app.id);
                  return (
                    <tr key={`${member.id}-${app.id}`}>
                      <td>{`${member.firstname} ${member.lastname}`}</td>
                      <td>{app.name}</td>
                      <td>{access ? access.access_level : "—"}</td>
                    </tr>
                  );
                })
              ))}
            </tbody>
          </table>
        </div>
      )
    }
  ];

  return (
    <div className={styles.container}>
      <div className={styles.title}>View Organization: {orgData.name}</div>
      {error && <div className={styles.error}>{error}</div>}
      <TabbedFormLayout
        tabs={tabs}
        onSave={() => {}}
        onCancel={() => navigate("/organizations")}
        showActions={false}
      />
    </div>
  );
}